/**
 * Client-side rate limit for fixed-phone activation code verification.
 * Persists across reload and SPA navigation; uses localStorage so limits align across tabs.
 */

export const ACTIVATION_CODE_MAX_FAILURES = 3;
export const ACTIVATION_CODE_COOLDOWN_MS = 120_000;

const STORAGE_KEY = "vle-activation-code-attempt-state";

export type ActivationCodeAttemptState = {
  failures: number;
  cooldownUntil: number | null;
};

type StoreShape = Record<string, ActivationCodeAttemptState>;

function normalizeScope(scope: string): string {
  const s = scope.trim().toLowerCase();
  return s.length > 0 ? s : "_";
}

function readAll(): StoreShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoreShape;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all: StoreShape): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // quota / private mode — rate limit degrades gracefully
  }
}

function getRaw(scope: string): ActivationCodeAttemptState {
  const key = normalizeScope(scope);
  const all = readAll();
  return all[key] ?? { failures: 0, cooldownUntil: null };
}

function setForScope(scope: string, state: ActivationCodeAttemptState): void {
  const key = normalizeScope(scope);
  const all = readAll();
  if (state.failures === 0 && state.cooldownUntil == null) {
    delete all[key];
  } else {
    all[key] = state;
  }
  writeAll(all);
}

/** If cooldown has ended, reset failures and persist (full reset for next tries). */
export function resolveActivationCodeAttemptState(
  scope: string,
): ActivationCodeAttemptState {
  const raw = getRaw(scope);
  const now = Date.now();
  if (raw.cooldownUntil != null && now >= raw.cooldownUntil) {
    const cleared: ActivationCodeAttemptState = {
      failures: 0,
      cooldownUntil: null,
    };
    setForScope(scope, cleared);
    return cleared;
  }
  return raw;
}

export function getActivationCodeAttemptStorageKey(): string {
  return STORAGE_KEY;
}

export function isClientErrorStatus(status: unknown): boolean {
  if (typeof status === "number") {
    return status >= 400 && status < 500;
  }
  return false;
}

/**
 * `getDeviceDetails` uses `transformErrorResponse`, so `unwrap()` may reject with a
 * formatted body (no HTTP status). Count those as failed verification attempts.
 * Do not count transport / server-down errors so users are not locked out unfairly.
 */
export function shouldCountActivationVerifyThrownFailure(
  error: unknown,
): boolean {
  if (error == null) return false;
  if (typeof error === "string") return error.trim().length > 0;
  if (typeof error !== "object") return false;
  const e = error as Record<string, unknown>;
  const status = e.status;
  if (
    status === "FETCH_ERROR" ||
    status === "PARSING_ERROR" ||
    status === "TIMEOUT_ERROR"
  ) {
    return false;
  }
  if (typeof status === "number") {
    if (status >= 500) return false;
    if (status >= 400) return true;
    return false;
  }
  if (typeof e.message === "string" && e.message.trim()) return true;
  if (typeof e.emailError === "string" && e.emailError.trim()) return true;
  if (typeof e.mobileError === "string" && e.mobileError.trim()) return true;
  return Object.keys(e).length > 0;
}

/** Successful verification — clear limiter for this scope. */
export function clearActivationCodeAttemptState(scope: string): void {
  setForScope(scope, { failures: 0, cooldownUntil: null });
}

/**
 * Record a failed verification. Starts cooldown immediately on the Nth failure.
 * No-op while an active cooldown is in effect (UI should block submits anyway).
 */
export function recordActivationCodeFailure(
  scope: string,
): ActivationCodeAttemptState {
  let current = resolveActivationCodeAttemptState(scope);
  const now = Date.now();
  if (current.cooldownUntil != null && now < current.cooldownUntil) {
    return current;
  }

  const failures = current.failures + 1;
  const cooldownUntil =
    failures >= ACTIVATION_CODE_MAX_FAILURES
      ? now + ACTIVATION_CODE_COOLDOWN_MS
      : null;

  const next: ActivationCodeAttemptState = { failures, cooldownUntil };
  setForScope(scope, next);
  return next;
}

export function getActivationCodeRateLimitSnapshot(scope: string): {
  failures: number;
  inCooldown: boolean;
  cooldownUntil: number | null;
  attemptsRemaining: number;
} {
  const { failures, cooldownUntil } = resolveActivationCodeAttemptState(scope);
  const now = Date.now();
  const inCooldown = cooldownUntil != null && now < cooldownUntil;

  const attemptsRemaining = inCooldown
    ? 0
    : Math.max(0, ACTIVATION_CODE_MAX_FAILURES - failures);

  return {
    failures,
    inCooldown,
    cooldownUntil,
    attemptsRemaining,
  };
}

export function remainingCooldownSeconds(
  cooldownUntil: number | null,
  nowMs: number = Date.now(),
): number {
  if (cooldownUntil == null || nowMs >= cooldownUntil) return 0;
  return Math.ceil((cooldownUntil - nowMs) / 1000);
}

// export function formatCooldownMinutesSeconds(totalSeconds: number): string {
//   const raw = Math.max(0, Math.floor(totalSeconds));
//   const displayTotal = raw > 1 ? raw - 1 : raw;
//   const minutes = Math.floor(displayTotal / 60);
//   const seconds = displayTotal % 60;
//   const minuteWord = minutes === 1 ? "minute" : "minutes";
//   const secondWord = seconds === 1 ? "second" : "seconds";

//   if (minutes === 0) {
//     return `${seconds} ${secondWord}`;
//   }
//   if (seconds === 0) {
//     return `${minutes} ${minuteWord}`;
//   }
//   return `${minutes} ${minuteWord} ${seconds} ${secondWord}`;

export function formatCooldownSeconds(totalSeconds: number): string {
  const n = Math.max(0, Math.ceil(Number(totalSeconds)));
  return `${n} ${n === 1 ? "second" : "seconds"}`;
}
